/* eslint-disable camelcase, no-console */
import { LitElement, html, property } from 'lit-element';
import bound from 'bound-decorator';
import style from './fc-form-field.css';
import toolTipStyle from '@forter/tooltip/fc-tooltip.css';
import tagsStyle from './fields/tags/form-tags-field.css.js';
import dropDownStyle from './fields/dropdown/form-dropdown-field.css.js';
import inputStyle from './fields/input/form-input-field.css.js';
import segmentedControlStyle from './fields/segmented-control/form-segmented-control-field.css.js';
import selectStyle from './fields/select/form-select-field.css.js';
import dateStyle from './fields/date/form-date-field.css.js';
import buttonGroupStyle from './fields/button-groups/form-button-group-field.css.js';

import { FIELD_TYPES, FIELDS_WITH_LABEL,
  getItemContentAndValue, isCustomType, removeItemFromArray } from './fc-form-field-helper';
import { Validators } from './fc-form-field-validations';
import { render_input } from './fields/input/form-input-field';
import { render_tags } from './fields/tags/form-tags-field';
import { render_date } from './fields/date/form-date-field';
import { render_chips } from './fields/chips/form-chips-field';
import { render_button_group } from './fields/button-groups/form-button-group-field';
import { render_select } from './fields/select/form-select-field';
import { render_textarea } from './fields/textarea/form-textarea-field';
import { render_segmented_control } from './fields/segmented-control/form-segmented-control-field';
import { render_radio_group } from './fields/radio-group/form-radio-group-field';
import { LifecycleEventsMixin } from '@forter/mixins/src/lifecycle-event-mixin';

const VALIDATIONS_BY_TYPES = {
  [FIELD_TYPES.NUMBER]: [Validators.max, Validators.min],
  [FIELD_TYPES.TEXT]: [Validators.maxlength, Validators.minlength],
};

/**
 * An element that contained field by different types
 * <!-- Author: oweingart <oweingart@forter.com> -->
 *
 * ## Usage
 *
 * ```html
 * <script>
 *   import '@forter/form-field';
 * </script>
 *
 * <fc-form-field>
 * </fc-form-field>
 * ```
 *
 * ## Examples
 * ```html
 * <!-- field options -->
 * <fc-form-field type="
 * " .options="${['foo','bar']}"></fc-form-field>
 * ```
 * ```html
 * <!-- input with warning -->
 * <fc-form-field type="number" label="foo" warning="Low Amount"></fc-form-field>
 * ```
 * ```html
 * <!-- select with warning -->
 * <fc-form-field type="dropdown" label="foo" warning="Low Amount"></fc-form-field>
 * ```
 *
 * @element fc-form-field
 * @cssprop --fc-form-field-disabled-color - color.
 * @cssprop --fc-form-field-disabled-color - disabled color. example: `gray`
 */
export class FcFormField extends LifecycleEventsMixin(LitElement) {
  /**
   * If the field is disabled
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) disabled = false;

  /**
   * If the field is readonly
   * @type {Boolean}
   * @attr
   */
   @property({ type: Boolean }) readonly = false;

  /**
   * If the field is required
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) required = false;


  /**
   * If multi field allow choosing only single value
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean, attribute: 'single-selection' }) singleSelection = false;

  /**
   * List of the validation name to run when value is changed
   * @type {Array}
   * @attr
   */
  @property({ type: Array }) validations;

  /**
   * Pattern validation
   * @type {Array}
   * @attr
   */
  @property({ type: String }) pattern;

  /**
   * Validate maxlength of string value
   * @type {Number}
   * @attr
   */
  @property({ type: Number }) maxlength;

  /**
   * Validate minimum length of string value
   * @type {Number}
   * @attr
   */
  @property({ type: Number }) minlength;

  /**
   * Validate minimum of number value
   * @type {Number}
   * @attr
   */
  @property({ type: Number }) min;

  /**
   * Validate minimum of number value
   * @type {Number}
   * @attr
   */
  @property({ type: Number }) max;

  /**
   * Type of the field - can be existing types such as: text, number, tags, dropdown or name of the type in the 'custom-renderers' object passed to fc-form.
   * @type {'text'|'tags'|'number'|'dropdown'|'date'|'button-group'|'currency'|'select' | 'textarea'|'chips'|'segmented-control'|'radio-group'}
   * @attr
   */
  @property({ type: String }) type = 'text';

  /**
   * Label of the field
   * @type {String}
   * @attr
   */
  @property({ type: String }) label;

  /**
   * Warning of the field content
   * @type {String}
   * @attr
   */
  @property({ type: String }) warning;

  /**
   * Placeholder in case of input types when no value
   * @type {String}
   * @attr
   */
  @property({ type: String }) placeholder;

  /**
   * List of all the available values in case of multiple option field such as dropdown or tags. example: `["foo","bar"]`
   * @type {Array}
   * @attr
   */
  @property({ type: Array }) options;

  /**
   * The value of the field in case of single value field (text, number, dropdown).
   * @type {String}
   * @attr
   */
  @property({ type: String }) value;

  /**
   * Date Format. default: `MM/DD/YYYY`, example: `DD MMM YYYY HH:mm`
   * @type {String}
   * @attr
   */
  @property({ type: String }) format;

  /**
   * List of selected values in case of multiple values field (tags)
   * @type {Array}
   * @attr
   */
  @property({ type: Array }) values;

  /**
   * Whether or not the chips menu is enabled
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) menu;

  /**
   * Type of the chips (example: "more")
   * @type {Boolean}
   * @attr
   */
  @property({ type: String, attribute: 'chips-type' }) chipsType;

  /**
   * Path of the the field what will be set on the fc-form model (example: "user.firstName", "user.email")
   * @type {String}
   * @attr
   */
  @property({ type: String }) path;

  /**
   * Currency symbol to show in currency type input field (example: "$", "₪")
   * @type {String}
   * @attr
   */
  @property({ type: String }) currencysymbol;

  /**
   * The additional "other" value in case of fields with multiple options that allow adding custom value
   * @type {String}
   * @attr
   */
  @property({ type: Boolean, attribute: 'allow-other-option' }) allowOtherOption;


  /**
   * The additional "other" value in case of fields with multiple options that allow adding custom value
   * @type {String}
   * @attr
   */
  @property({ type: String, attribute: 'other-option-value' }) otherOptionValue;

  /**
   * For date input, additional options for the date picker to pass
   * @type {Object}
   * @attr
   */
  @property({ type: Object }) dateOptions = {};

  /**
   *
   * Internal Observables
   *
   */

  /**
   * If the field is valid
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) valid = true;

  /**
   * If the field is dirty
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) dirty = false;

  /**
   * If the field is clearable
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) clearable = false;

  /**
   * If the field is boldable
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) boldable = false;

  /**
   * If hide resize in textarea
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) hideResize = false;

  /**
   * Rows in textarea
   * @type {Boolean}
   * @attr
   */
  @property({ type: String }) rows = '2';

  /**
   * If fc-input width should be extended
   * @type {Boolean}
   * @attr
   */
  @property({ type: Boolean }) extended = false;

  /** @inheritdoc */
  static is = 'fc-form-field';

  /** @inheritdoc */
  static styles = [
    style,
    dropDownStyle,
    tagsStyle,
    inputStyle,
    dateStyle,
    buttonGroupStyle,
    toolTipStyle,
    segmentedControlStyle,
    selectStyle
  ];

  constructor() {
    super();
    this._wasRendered = false;
  }

  @bound disconnectedCallback() {
    super.disconnectedCallback();
    const { type, path } = this;
    if (this.optionsObserver) {
      this.optionsObserver.disconnect();
    }
    this.dispatchEvent(new CustomEvent('field-removed', {
      detail: { type, path },
    }));
  }

  /**
   * Run validations functions on the element after the change
   * @return {[]}
   */
  @bound getValidationErrors() {
    const { validations = [], customValidators, type } = this;
    const errors = [];
    validations.forEach(validationName => {
      const validationFunction = Validators[validationName] || customValidators[validationName];
      if (!validationFunction) return;
      const error = validationFunction(this);
      error && errors.push(error);
    });
    (VALIDATIONS_BY_TYPES[type] || []).forEach((validationFunction => {
      const error = validationFunction(this);
      error && errors.push(error);
    }));
    return errors;
  }

  /**
   * Fire change event
   * @param {String} eventName
   */
  @bound fireChangeEvent(eventName) {
    const item = this.__itemInChange;
    delete this.__itemInChange;
    const { type, path, values, valid, value, validationErrors } = this;
    this.dispatchEvent(new CustomEvent(eventName, {
      detail: { type, path, item, values, value, valid, validationErrors },
    }));
  }

  /**
   *
   * Single Value methods:
   *
   */

  /**
   * Set single value field
   * @param {Event} eventOrValue
   */
  @bound setValue(eventOrValue) {
    const { disabled } = this;
    if (disabled) return;
    this.value = eventOrValue && eventOrValue.target ? eventOrValue.target.value : eventOrValue;
  }

  /**
   * Getter for single value field
   * @return {*}
   */
  get value() {
    return this.__value;
  }

  /**
   * Setter for single value field
   * @param {String} value
   */
  set value(value) {
    this.__value = value;
    this.dirty = true;
    this.validationErrors = this.getValidationErrors();
    this.valid = (this.validationErrors || []).length < 1;
    this.requestUpdate();
    this.fireChangeEvent('change-value');
  }

  /**
   * Getter for required
   * @return {*}
   */
  get required() {
    return this.__required;
  }

  /**
   * Setter for single value field
   * @param {String} value
   */
  set required(value) {
    this.__required = value;
    this.requestUpdate();
    this.fireChangeEvent('required-change');
  }

  /**
   *
   * Multi Values methods:
   *
   */

  /**
   * Add value to list of values
   * @param {String} item
   */
  @bound addValue(item) {
    const { disabled } = this;
    if (disabled) return;
    let { values } = this;
    values = values || [];
    this.values = [...values, item];
    this.__itemInChange = item; // hack to reduce search time - as we already know what was the change
  }

  @bound setValues(items = []) {
    const { disabled } = this;
    if (disabled) return;

    this.values = [...items];
  }

  /**
   * Remove value to list of values
   * @param {String} item
   */
  @bound removeValue(item) {
    const { disabled } = this;
    if (disabled) return;
    let { values } = this;
    values = values || [];
    removeItemFromArray(values, item);
    this.values = [...values];
    this.__itemInChange = item; // hack to reduce search time - as we already know what was the change
  }

  /**
   * Getter for multi values field
   * @return {*}
   */
  get options() {
    if (!this.__options) {
      this.__options = Array.from(this.querySelectorAll('option')).map(e => e.value);
    }
    return this.__options;
  }

  set options(options) {
    this.__options = options;
    this.requestUpdate();
  }

  /**
   * Getter for multi values field
   * @return {*}
   */
  get values() {
    return this.__values;
  }

  /**
   * Setter for multi values field
   * @param {Array} values
   */
  set values(values) {
    if (this.allowOtherOption) {
      const otherValue = (values || []).find(d => !(this.options || []).includes(d));
      this.otherOptionValue = otherValue ? otherValue : null;
    }
    if (!this.__itemInChange) {
      this.__itemInChange = (values || []).filter(d => !(this.__values || []).includes(d));
    }
    this.__values = values;
    this.dirty = true;
    this.validationErrors = this.getValidationErrors();
    this.valid = (this.validationErrors || []).length < 1;
    this.requestUpdate();
    this.fireChangeEvent('change-values');
  }

  @bound getValue() {
    const { value, values } = this;
    if (value) {
      return value;
    }
    if (values) {
      return values;
    }
  }

  /**
   * Render the label
   * @return {*}
   */
  @bound renderLabel() {
    const { label, required, validationErrors, renderErrorMessage, warning } = this;
    return html`
    <label ?required="${required}">${label}
      ${warning ? html`<fc-chip>${warning}</fc-chip>` : ''}
      <slot name="label"></slot>
        ${renderErrorMessage ? (validationErrors || []).map(errorEnum => html`<span class="label-error">${renderErrorMessage[errorEnum || 'default'](this, errorEnum)}</span>`) : ''}
    </label>`;
  }

  /**
   * Render the field by type
   * @return {*}
   */
  @bound renderField() {
    const { type, customRenderers } = this;
    const useCustomRender = isCustomType(type) && customRenderers && customRenderers[type];
    if (useCustomRender) {
      return customRenderers[type](this);
    } else {
      switch (type) {
        case FIELD_TYPES.TAGS:
          return render_tags.call(this);
        case FIELD_TYPES.TEXT:
          return render_input.call(this);
        case FIELD_TYPES.NUMBER:
          return render_input.call(this);
        case FIELD_TYPES.CURRENCY:
          return render_input.call(this);
        case FIELD_TYPES.DROPDOWN:
          return render_select.call(this);
        case FIELD_TYPES.DATE:
          return render_date.call(this);
        case FIELD_TYPES.BUTTON_GROUP:
          return render_button_group.call(this);
        case FIELD_TYPES.SELECT:
          return render_select.call(this);
        case FIELD_TYPES.TEXTAREA:
          return render_textarea.call(this);
        case FIELD_TYPES.CHIPS:
          return render_chips.call(this);
        case FIELD_TYPES.SEGMENTED_CONTROL:
          return render_segmented_control.call(this);
        case FIELD_TYPES.RADIO_GROUP:
          return render_radio_group.call(this);
        default:
          console.warn(`fc-form-field: field type: ${type} was not found!`);
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();
    const slottedOptions = Array.from(this.querySelectorAll('option'));
    this.setDefaultIfNeeded(slottedOptions);
    this.observerOptionChangeIfNeeded(slottedOptions);
  }

  @bound observerOptionChangeIfNeeded(slottedOptions) {
    if (slottedOptions.length) {
      this.optionsObserver = new MutationObserver(() => {
        this.requestUpdate();
      });
      this.optionsObserver.observe(this, { childList: true });
    }
  }

  @bound setDefaultIfNeeded(slottedOptions) {
    const { values, type } = this;
    if (slottedOptions.length) {
      const defaultValue = slottedOptions.find(x => x.hasAttribute('default'));
      if (defaultValue && (!values || !values.length)) {
        const { itemValue } = getItemContentAndValue(defaultValue);
        if (type === FIELD_TYPES.DROPDOWN) {
          this.setValue(itemValue);
        } else {
          this.addValue(itemValue);
        }
      }
    }
  }

  /** @inheritdoc */
  render() {
    const { type } = this;
    const fieldTemplate = this.renderField();
    const labelTemplate = this.renderLabel();
    return html`
    ${!FIELDS_WITH_LABEL.includes(type) ? labelTemplate : ''}
    ${fieldTemplate}
    `;
  }
}
